import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../App";
import DesktopQuickTour from "./onboarding/DesktopQuickTour";
import MobileQuickTour from "./onboarding/MobileQuickTour";
import TrialWelcomeModal from "./onboarding/TrialWelcomeModal";

/**
 * OnboardingProvider — orchestrates the post-signup celebration modal
 * AND the Quick Tour. Sequence:
 *
 *   1. /onboarding/status reports trial_welcome_shown=false → render
 *      TrialWelcomeModal (Congratulations!). On dismiss we POST
 *      /onboarding/trial-welcome-shown so it never re-renders.
 *   2. After the welcome modal closes (or if already shown), if
 *      has_completed_onboarding=false the device-specific QuickTour
 *      renders. Completion persists via the existing
 *      /onboarding/complete-step endpoint.
 *
 * Both flags are server-driven so a user logging in on a different
 * browser still sees the right step.
 */

const LS_KEY = "tadlo_onboarding_completed";
const LS_KEY_WELCOME = "tadlo_trial_welcome_shown";
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export default function OnboardingProvider() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("tadlo_token");
      if (!token) return;
      // May 2026 — DB flag is the single source of truth. Old code
      // short-circuited on localStorage cache; that flag clears on
      // incognito / new device, causing the tour to re-appear on
      // every login. We always hit the API now and only use the
      // localStorage flags as a per-tab cache to avoid duplicate
      // calls within the same session.
      const { data } = await api.get("/onboarding/status");
      // Welcome modal — gate first, before the tour.
      if (!data?.trial_welcome_shown) {
        setTimeout(() => setWelcomeOpen(true), 1200);
        return;
      }
      localStorage.setItem(LS_KEY_WELCOME, "1");
      if (data?.has_completed_onboarding) {
        localStorage.setItem(LS_KEY, "1");
        return;
      }
      // Small delay so the dashboard lands first
      setTimeout(() => setIsOpen(true), 1200);
    } catch {
      // Silent — onboarding is non-critical
    }
  }, []);

  useEffect(() => {
    if (location.pathname === "/dashboard" && !hasChecked) {
      setHasChecked(true);
      checkStatus();
    }
  }, [location.pathname, hasChecked, checkStatus]);

  const markWelcomeShown = useCallback(async () => {
    localStorage.setItem(LS_KEY_WELCOME, "1");
    setWelcomeOpen(false);
    // Toast confirmation
    try {
      toast.success("Your free plan is active — unlimited DMs, no card", {
        description: "You have unlimited DMs. 🎉",
        duration: 5000,
        className: "bg-emerald-600 text-white border-emerald-700",
      });
    } catch {
      // toast lib not available — non-critical
    }
    try {
      await api.post("/onboarding/trial-welcome-shown");
    } catch {
      // Silent — local cache is enough to prevent re-show
    }
    // Open the product tour next (unless already completed).
    try {
      const { data } = await api.get("/onboarding/status");
      if (!data?.has_completed_onboarding) {
        setTimeout(() => setIsOpen(true), 300);
      } else {
        localStorage.setItem(LS_KEY, "1");
      }
    } catch {
      // ignore
    }
  }, []);

  const markComplete = useCallback(async () => {
    localStorage.setItem(LS_KEY, "1");
    setIsOpen(false);
    try {
      // May 2026 — call /onboarding/skip instead of /complete-step.
      // ``complete-step`` only flips ``has_completed_onboarding`` to
      // True once EVERY step in ``ONBOARDING_STEPS`` is checked off,
      // so completing a single tour step never marked the user done
      // and the tour kept re-appearing on every login. ``skip``
      // unconditionally sets ``has_completed_onboarding: true`` —
      // exactly what we want whether the user finished or dismissed
      // the tour.
      await api.post("/onboarding/skip");
    } catch {
      // Silent — local cache is enough to prevent re-show this session
    }
  }, []);

  return (
    <AnimatePresence>
      {welcomeOpen && (
        <TrialWelcomeModal key="trial-welcome" onStart={markWelcomeShown} />
      )}
      {isOpen && !welcomeOpen && (
        isMobile ? (
          <MobileQuickTour
            key="mobile"
            onComplete={markComplete}
            onSkip={markComplete}
          />
        ) : (
          <DesktopQuickTour
            key="desktop"
            onClose={markComplete}
            onGetStarted={markComplete}
          />
        )
      )}
    </AnimatePresence>
  );
}
